/**
 * Vue shell for comment-thread UI state.
 *
 * TEA: pure Model/Msg in `useCommentThreads.update.ts`. The active editor's
 * `CommentSurface` (open-thread highlight, reveal, delete), text mutations,
 * reveal timers and layout stay here as Cmds. Comments are `.zwdoc`-only
 * (#251 / #260).
 */
import { computed, watch, type ComputedRef, type Ref } from "vue";
import { commentAnchorIdsOf } from "../editor/commentDoc";
import {
  appendReply,
  deleteComment,
  deleteMessage,
  setCommentResolved,
  updateMessage,
} from "../editor/commentMutations";
import { readCommentStore } from "../editor/commentStore";
import type { CommentSurface } from "../editor/commentSurface";
import { assertNever } from "../lib/assertNever";
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { useTea } from "../lib/tea";
import { createTimeoutHandle } from "../lib/timeoutHandle";
import {
  initialCommentThreadsModel,
  updateCommentThreadsFn,
  type CommentThreadsCmd,
} from "./useCommentThreads.update";

/** Comment thread state: which threads exist, which is open/active/hovered,
 * and the mutation handlers the CommentMargin panel drives. Card
 * positioning (where each thread sits on screen) is a separate concern.
 * This composable just calls the `scheduleLayout` callback it's given
 * whenever something changes that affects placement. */
export function useCommentThreads(options: {
  getSurface: () => Maybe<CommentSurface>;
  doc: Ref<string>;
  activeCommentId: Ref<Maybe<string>>;
  mobileShell: Ref<boolean>;
  commentsOpen: Ref<boolean>;
  scheduleLayout: () => void;
  emitSelectComment: (id: string) => void;
}): {
  activeId: Ref<Maybe<string>>;
  openIds: Ref<Set<string>>;
  openId: ComputedRef<Maybe<string>>;
  hoveredCommentId: Ref<Maybe<string>>;
  commentsVisible: Ref<boolean>;
  setActiveId: (idM: Maybe<string>) => void;
  syncOpenThreads: (docText: string) => void;
  openThread: (id: string) => void;
  closeThread: (id: string) => void;
  scrollToComment: (id: string, focusLine?: boolean) => void;
  onSelectComment: (id: string, focusLine?: boolean) => void;
  onReply: (threadId: string, body: string) => void;
  onUpdateMessage: (threadId: string, messageId: string, body: string) => void;
  onDeleteMessage: (threadId: string, messageId: string) => void;
  onToggleResolved: (id: string) => void;
  onRemove: (id: string) => void;
  onCommentTextClick: (id: string) => boolean;
  scheduleReveal: (delayMs: number) => void;
  cleanup: () => void;
} {
  const commentsRevealTimer = createTimeoutHandle();

  function commitDoc(next: string): void {
    if (next === options.doc.value) return;
    options.doc.value = next;
  }

  function syncOpenCommentFacet(idM: Maybe<string>): void {
    const surface = options.getSurface();
    if (isJust(surface)) surface.value.setOpenComment(fromMaybe(idM));
  }

  function scrollToComment(id: string, focusLine = true): void {
    const surface = options.getSurface();
    if (isJust(surface)) surface.value.revealComment(id, focusLine);
  }

  function runCmd(cmd: CommentThreadsCmd): void {
    switch (cmd.kind) {
      case "bridgeActiveCommentId":
        options.activeCommentId.value = cmd.id;
        return;
      case "syncOpenCommentFacet":
        syncOpenCommentFacet(cmd.id);
        return;
      case "scheduleLayout":
        options.scheduleLayout();
        return;
      case "openMobileComments":
        options.commentsOpen.value = true;
        return;
      case "emitSelectComment":
        options.emitSelectComment(cmd.id);
        return;
      case "scrollToComment":
        scrollToComment(cmd.id, true);
        return;
      case "flashComment": {
        const surface = options.getSurface();
        if (isJust(surface)) surface.value.flashComment(cmd.id);
        return;
      }
      case "scheduleReveal":
        commentsRevealTimer.schedule(cmd.delayMs, () => {
          void dispatch({ type: "RevealComments" });
        });
        return;
      default:
        assertNever(cmd);
    }
  }

  const { model, dispatch } = useTea(
    initialCommentThreadsModel(options.activeCommentId.value),
    updateCommentThreadsFn,
    { runCmd: just(runCmd) },
  );

  // Another surface (e.g. the comment margin's own selection) may drive
  // activeCommentId directly. Keep the local mirror in sync.
  watch(
    () => options.activeCommentId.value,
    (m) => {
      void dispatch({ type: "ActiveIdSynced", id: m });
    },
  );

  const activeId = computed(() => model.value.activeId);
  const openId = computed(() => model.value.openId);
  const openIds = computed((): Set<string> =>
    isJust(model.value.openId)
      ? new Set([model.value.openId.value])
      : new Set(),
  );
  const hoveredCommentId = computed({
    get: (): Maybe<string> => model.value.hoveredCommentId,
    set: (id: Maybe<string>): void => {
      void dispatch({ type: "SetHovered", id });
    },
  });
  const commentsVisible = computed({
    get: (): boolean => model.value.commentsVisible,
    set: (visible: boolean): void => {
      void dispatch({ type: "SetCommentsVisible", visible });
    },
  });

  function setActiveId(idM: Maybe<string>): void {
    void dispatch({ type: "SetActiveId", id: idM });
  }

  function syncOpenThreads(docText: string): void {
    void dispatch({
      type: "SyncOpenThreads",
      anchorIds: commentAnchorIdsOf(docText),
    });
  }

  function openThread(id: string): void {
    void dispatch({
      type: "OpenThread",
      id,
      mobileShell: options.mobileShell.value,
    });
  }

  function closeThread(id: string): void {
    void dispatch({ type: "CloseThread", id });
  }

  function onSelectComment(id: string, focusLine = false): void {
    void dispatch({
      type: "SelectComment",
      id,
      focusLine,
      mobileShell: options.mobileShell.value,
    });
  }

  function onReply(threadId: string, body: string): void {
    commitDoc(appendReply(options.doc.value, threadId, body));
    void dispatch({
      type: "ReplyOpened",
      threadId,
      mobileShell: options.mobileShell.value,
    });
  }

  function onUpdateMessage(
    threadId: string,
    messageId: string,
    body: string,
  ): void {
    commitDoc(updateMessage(options.doc.value, threadId, messageId, body));
  }

  function onDeleteMessage(threadId: string, messageId: string): void {
    const thread = readCommentStore(options.doc.value)[threadId];
    const remaining =
      thread?.messages.filter((m) => m.id !== messageId).length ?? 0;

    const surface = options.getSurface();
    if (isJust(surface) && remaining === 0) {
      surface.value.removeComment(threadId);
      void dispatch({ type: "ClearActiveIf", id: threadId });
      return;
    }

    const next = deleteMessage(options.doc.value, threadId, messageId);
    commitDoc(next);
    if (isNothing(toMaybe(readCommentStore(next)[threadId]))) {
      void dispatch({ type: "ClearActiveIf", id: threadId });
    }
  }

  function onToggleResolved(id: string): void {
    const resolved = readCommentStore(options.doc.value)[id]?.resolved ?? false;
    commitDoc(setCommentResolved(options.doc.value, id, resolved === false));
  }

  function onRemove(id: string): void {
    const surface = options.getSurface();
    if (isJust(surface)) {
      surface.value.removeComment(id);
    } else {
      commitDoc(deleteComment(options.doc.value, id));
    }
    void dispatch({ type: "ThreadRemoved", id });
  }

  /** Called from the doc-click handler registered in onMounted. */
  function onCommentTextClick(id: string): boolean {
    if (isJust(model.value.openId) && model.value.openId.value === id) {
      return false;
    }
    void dispatch({
      type: "CommentTextClick",
      id,
      mobileShell: options.mobileShell.value,
    });
    return true;
  }

  /** Reveal the comment layer after the editor's opening shift settles. */
  function scheduleReveal(delayMs: number): void {
    void dispatch({ type: "ScheduleReveal", delayMs });
  }

  function cleanup(): void {
    commentsRevealTimer.clear();
  }

  return {
    activeId,
    openIds,
    openId,
    hoveredCommentId,
    commentsVisible,
    setActiveId,
    syncOpenThreads,
    openThread,
    closeThread,
    scrollToComment,
    onSelectComment,
    onReply,
    onUpdateMessage,
    onDeleteMessage,
    onToggleResolved,
    onRemove,
    onCommentTextClick,
    scheduleReveal,
    cleanup,
  };
}
